import { useState, useEffect, useRef } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { Header } from './Header';
import { MobileNav } from './mobile-nav';
import { UpdateBanner } from './UpdateBanner';
import { EnablePushBanner } from './EnablePushBanner';
import { AssistantWidget } from '@/components/assistant/assistant-widget';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from '@/components/ui/sheet';
import { Toaster } from 'sonner';
import { TooltipProvider } from '@/components/ui/tooltip';
import { PageTitleProvider } from '@/contexts/page-title-context';
import { ColumnPolicyProvider } from '@/components/wms-table/column-policy';
import { cn } from '@/lib/utils';
import { useWarehouses } from '@/api/hooks/useWarehouses';
import { useWarehouseStore } from '@/stores/warehouse-store';
import { useChannelStore } from '@/stores/channel-store';
import { useActiveWarehouse } from '@/hooks/useActiveWarehouse';
import { deriveWarehouseRouteKey } from '@/lib/warehouse-route-key';
import type { Warehouse } from '@/types';
import type { Warehouse as ApiWarehouse } from '@/api/types/common';

// Map API warehouse to store warehouse format
function mapApiWarehouse(apiWarehouse: ApiWarehouse): Warehouse {
  return {
    id: apiWarehouse.id,
    code: apiWarehouse.code,
    name: apiWarehouse.name,
    address1: apiWarehouse.address ?? undefined,
    city: apiWarehouse.city ?? undefined,
    state: apiWarehouse.state ?? undefined,
    postalCode: apiWarehouse.postalCode ?? undefined,
    country: apiWarehouse.country ?? undefined,
    timezone: apiWarehouse.timezone ?? undefined,
    isActive: apiWarehouse.isActive,
    createdAt: apiWarehouse.createdAt,
    updatedAt: apiWarehouse.updatedAt,
  };
}

/**
 * Manufacturing floor screens (tablets at the saws, the scan station and the
 * end of the line): the sidebar starts collapsed on them so the work fills
 * the screen (David, 2026-11-02: "this is for tablet, so it needs to be full
 * screen, and I want the sidebar hidden by default when they come to this
 * screen"). The rail keeps its expand button; leaving the screens restores
 * what the sidebar was before (a manager on a desktop is not left with a
 * folded menu), and a manual toggle on a floor screen is kept there.
 */
const FLOOR_ROUTE_PREFIXES = [
  '/manufacturing-count',
  '/manufacturing-work-orders',
  '/manufacturing-scan',
];
const isFloorRoute = (pathname: string) =>
  FLOOR_ROUTE_PREFIXES.some(
    (p) => pathname === p || pathname.startsWith(`${p}/`),
  );

export function MainLayout() {
  const { pathname } = useLocation();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() =>
    isFloorRoute(pathname),
  );
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  // Collapse on ENTERING a floor screen (not on every render there, so the
  // user can still expand the rail while working); restore on leaving.
  const wasFloor = useRef(isFloorRoute(pathname));
  const restoreTo = useRef<boolean | null>(null);
  useEffect(() => {
    const floor = isFloorRoute(pathname);
    if (floor && !wasFloor.current) {
      setSidebarCollapsed((c) => {
        restoreTo.current = c;
        return true;
      });
    } else if (!floor && wasFloor.current && restoreTo.current !== null) {
      setSidebarCollapsed(restoreTo.current);
      restoreTo.current = null;
    }
    wasFloor.current = floor;
  }, [pathname]);
  const toggleSidebar = () => {
    restoreTo.current = null; // the user chose: nothing to restore
    setSidebarCollapsed((c) => !c);
  };

  // Still fetch all warehouses for the full Warehouse objects (needed for selectedWarehouse)
  const { data: warehousesData } = useWarehouses();
  const setWarehouses = useWarehouseStore((state) => state.setWarehouses);
  const { isAllMode, activeWarehouseId } = useActiveWarehouse();
  // Channel joins the remount key: resetWarehouseScopedCache() is
  // queryClient.clear(), which only refetches when paired with a remount —
  // TanStack does not refetch an observer whose query is cleared under it.
  const selectedChannel = useChannelStore((s) => s.selectedChannel);
  const warehouseRouteKey = `${deriveWarehouseRouteKey({
    isAllMode,
    activeWarehouseId,
  })}:${selectedChannel}`;

  useEffect(() => {
    if (warehousesData?.data && warehousesData.data.length > 0) {
      setWarehouses(warehousesData.data.map(mapApiWarehouse));
    }
  }, [warehousesData, setWarehouses]);

  return (
    <TooltipProvider>
      <PageTitleProvider>
        <ColumnPolicyProvider>
          <div className="flex h-screen overflow-hidden bg-background">
            {/* Desktop sidebar */}
            <div className="hidden lg:block">
              <Sidebar collapsed={sidebarCollapsed} onToggle={toggleSidebar} />
            </div>

            {/* Mobile sidebar */}
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetContent side="left" className="p-0 w-64">
                <SheetTitle className="sr-only">Main menu</SheetTitle>
                <SheetDescription className="sr-only">
                  Navigate warehouse tasks, settings, and the knowledge base.
                </SheetDescription>
                <Sidebar
                  collapsed={false}
                  inlineSubmenus
                  onToggle={() => setMobileMenuOpen(false)}
                  onNavigate={() => setMobileMenuOpen(false)}
                />
              </SheetContent>
            </Sheet>

            {/* Main content */}
            <div className="flex flex-1 flex-col overflow-hidden">
              <UpdateBanner />
              <EnablePushBanner />
              <Header
                showMenuButton
                onMenuClick={() => setMobileMenuOpen(true)}
              />
              <main
                data-testid="main-content"
                className={cn(
                  'flex-1 overflow-y-auto md:flex md:flex-col p-4 pb-20 lg:p-6 lg:pb-6',
                  'transition-all duration-300',
                )}
              >
                <Outlet key={warehouseRouteKey} />
              </main>
            </div>

            {/* Mobile Bottom Navigation */}
            <MobileNav
              onMoreClick={() => setMobileMenuOpen(true)}
              moreOpen={mobileMenuOpen}
            />

            {/* WMS Analyst launcher (admins; hidden on /assistant itself) */}
            <AssistantWidget />

            <Toaster position="top-right" richColors closeButton />
          </div>
        </ColumnPolicyProvider>
      </PageTitleProvider>
    </TooltipProvider>
  );
}
